import React, { createContext, useContext, useEffect, useState } from 'react';
import { SWRConfig } from 'swr';
import { HTTP_STATUS } from '../../config/api';
import { AuthError, UseAuthError, useAuthError } from './use-auth-error';
import { useEventPublicInfo } from '../../hooks/api/public/use-event-public-details';
import { authenticate, getAccessToken, handlePassportAuthTokens, invalidate, removeTokens, validateCurrentAuthenticatedUser } from '../auth';
import { AppContextType, useApp } from './app-config-context';
import { normalizeRoutePath } from '../helpers/urlHelper';
import { ROOT } from '../../config/routes/paths';
import { usePathPrefix } from '../../hooks/use-path-prefix';
import { PassportAuthTokens } from '../api';
import { getSSOAuthLogin } from '../api/public';

export type IAuthContext = {
  isAuthenticated: boolean | null,
  authError: AuthError,
  resetAuthError(): void,
  signIn(email: string, accessCode: string, ssoEnabled?: boolean): Promise<boolean>,
  signOut(): Promise<void>,
  forceSignOut(): Promise<void>,
  /** Removes stored tokens and marks the user unauthenticated without navigation (preserves URL). */
  clearLocalSession(): void,
  passwordlessAuthInProgress: boolean,
  invalidLink: boolean,
  resetInvalidLink(): void,
  setInvalidLink(): void,
  setAsAuthenticated(): void,
  setAsNotAuthenticated(): void,
  setPasswordlessAuthInProgress(): void,
  resetPasswordlessAuthInProgress(): void,
  isLoading: boolean;
  /** True once the user has submitted the sign-in form; prevents the registration-as-landing redirect from firing in reaction to a failed sign-in. */
  signInAttempted: boolean;
}

const useAuthProvider = () => {
  const [isAuthenticated, setAuthStatus] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [passwordlessAuthInProgress, setupPasswordlessAuthInProgress] = useState<boolean>(true);
  const [invalidLink, setupInvalidLink] = useState<boolean>(false);
  const [signInAttempted, setSignInAttempted] = useState<boolean>(false);

  const setAsAuthenticated = (): void => setAuthStatus(true);
  const setAsNotAuthenticated = (): void => setAuthStatus(false);

  const { error: authError, setError } = useAuthError() as UseAuthError;
  const resetAuthError = () => setError(null);
  const resetInvalidLink = () => setupInvalidLink(false);
  const setInvalidLink = () => setupInvalidLink(true);
  const setPasswordlessAuthInProgress = () => setupPasswordlessAuthInProgress(true);
  const resetPasswordlessAuthInProgress = () => setupPasswordlessAuthInProgress(false);

  const { data } = useEventPublicInfo();
  const { isConfigured } = useApp() as AppContextType;

  const pathPrefix = usePathPrefix();
  const rootPath = normalizeRoutePath(ROOT, pathPrefix);

  useEffect(() => {
    setIsLoading(true);

    if (isConfigured) {
      validateCurrentAuthenticatedUser()
        .then(setAsAuthenticated)
        .catch(() => {
          removeTokens();
          setAsNotAuthenticated();
        })
        .finally(() => {
          resetPasswordlessAuthInProgress();
          setIsLoading(false);
        });
    }

    if (isConfigured === false) {
      setAsNotAuthenticated();
    }

  }, [isConfigured]);

  const signIn = async (email: string, accessCode: string, ssoEnabled?: boolean): Promise<boolean> => {
    /* Mark the attempt up front and keep it set even on failure (OEP-26851): it must persist `true`
       so the registration-as-landing redirect stays suppressed while the user retries on the Sign In
       form. It is cleared only on sign-out/session reset (resetData / clearLocalSession). */
    setSignInAttempted(true);
    setIsLoading(true);
    const username = `${email.trim()}_${data?.eventId}`;

    try {
      if (ssoEnabled) {
        const authHeaderValue = btoa(`${username}:${accessCode}`);
        const response = await getSSOAuthLogin(authHeaderValue);
        if (response?.fusionAuthIdpRedirectUrl) {
          window.location.href = response.fusionAuthIdpRedirectUrl;
          return true;
        }

        if (response?.accessToken) {
          handlePassportAuthTokens(response as PassportAuthTokens);
          setAsAuthenticated();
          return true;
        }
        setAsNotAuthenticated();
        return false;
      }

      await authenticate(username, accessCode);
      setAsAuthenticated();
      return true;
    } catch (e) {
      setAsNotAuthenticated();
      setError(e as AuthError);
      resetPasswordlessAuthInProgress();
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resetData = () => {
    resetInvalidLink();
    resetPasswordlessAuthInProgress();
    setSignInAttempted(false);
    removeTokens();
    window.history.replaceState(null, '', rootPath);
  };

  const signOut = async (): Promise<void> => {
    try {
      await invalidate();
    } finally {
      setAsNotAuthenticated();
      resetData();
      window.location.replace(rootPath);
    }
  };

  const forceSignOut = async (): Promise<void> => {
    setAsNotAuthenticated();
    resetData();
  };

  const clearLocalSession = (): void => {
    resetInvalidLink();
    resetPasswordlessAuthInProgress();
    setSignInAttempted(false);
    removeTokens();
    setAsNotAuthenticated();
  };

  return {
    isAuthenticated, authError, resetAuthError,
    signIn, signOut, forceSignOut, clearLocalSession,
    passwordlessAuthInProgress, invalidLink,
    resetInvalidLink,
    setAsAuthenticated,
    setAsNotAuthenticated,
    setInvalidLink,
    setPasswordlessAuthInProgress,
    resetPasswordlessAuthInProgress,
    isLoading,
    signInAttempted
  };
};
const authContext = createContext<IAuthContext | void>(undefined);
type Props = {
  children: React.ReactNode | React.ReactNodeArray
}
export const AuthProvider: React.FC<Props> = ({ children }: Props): JSX.Element => {
  const auth = useAuthProvider() as IAuthContext;
  const onError = async (error: AuthError): Promise<void> => {
    const shouldSignOut = (error?.status === HTTP_STATUS.UNAUTHORIZED || error?.status === HTTP_STATUS.FORBIDDEN) && !getAccessToken();

    if (shouldSignOut) await auth.forceSignOut();
  };
  return (
    <authContext.Provider value={auth}>
      <SWRConfig value={{ onError }}>
        {children}
      </SWRConfig>
    </authContext.Provider>
  );
};
export const useAuth = (): IAuthContext | void => useContext(authContext);
export default AuthProvider;